// src/components/Keikkakalenteri/Keikkakalenteri.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import fiLocale from "@fullcalendar/core/locales/fi";
import PropTypes from "prop-types";
import { api } from "../../services/api";

// kuinka pitkälle etsitään vapaita pe/la – vaikuttaa vain verhoon/merkkiin
const WINDOW_DAYS = 30;

const Keikkakalenteri = ({
  bandId,
  companyId,
  onDateSelect,
  refreshKey,
  initialDate,
  enableMobileScroll = true,
  scrollTargetSelector,
  onHasMarkedChange,
 

}) => {
  const [events, setEvents] = useState([]);
  // 🔹 Valittu päivä jää näkyviin “muurahais”-korostuksella
  
  const [viewRange, setViewRange] = useState({ start: null, end: null });

  const lastRangeRef = useRef({ start: null, end: null });
  const prevSigRef = useRef("");
  const availRef = useRef({}); // availability talteen klikkia varten

  // nosta callback refiin
  const onHasMarkedChangeRef = useRef(onHasMarkedChange);
  useEffect(() => { onHasMarkedChangeRef.current = onHasMarkedChange; }, [onHasMarkedChange]);

  const earliestOpenRef = useRef(null); // mahdollinen jatkokäyttö

  // “aktiivinen” päivä
  const [selectedDate, setSelectedDate] = useState(null);
  const cellsRef = useRef(new Map());
  const containerRef = useRef(null);


  const todayStr = useMemo(() => {
    const t = new Date(); t.setHours(0, 0, 0, 0);
    const y = t.getFullYear();
    const m = String(t.getMonth() + 1).padStart(2, "0");
    const d = String(t.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, []);

  const monthStartStr = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    t.setDate(1);
    const y = t.getFullYear();
    const m = String(t.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}-01`;
  }, []);

 const calendarInitialDate = useMemo(
   () => (initialDate ? initialDate : monthStartStr),
   [initialDate, monthStartStr]
 );

 // hyväksyttävät statukset verhoa varten
const STATUS_OK = new Set(["open", "pending", "booked"]);

// Palauta päivän "paras" status events-listasta.
// Jos päivälle osuu blackout/closed, se EI kelpaa.
// Muuten priorisoi booked > pending > open.
const getStatusOnDate = (events, ymd) => {
  const hits = events.filter(e => e.start <= ymd && ymd < (e.end || e.start));
  if (!hits.length) return null;

  // jos yksikin on blackout/closed, päivä ei kelpaa
  if (hits.some(e => {
    const st = e?.extendedProps?.status || e.status;
    return st === "blackout" || st === "closed";
  })) return "blocked";

  // priorisoi booked > pending > open
  const prio = { booked: 3, pending: 2, open: 1 };
  let best = null, bestScore = -1;
  for (const e of hits) {
    const st = (e?.extendedProps?.status || e.status || "").toLowerCase();
    const sc = prio[st] ?? -1;
    if (sc > bestScore) { best = st; bestScore = sc; }
  }
  return best; // "booked" | "pending" | "open" | null
};

// Tarkista löytyykö tänään..+N sisällä pe–la -pari, jossa molemmat STATUS_OK
const hasFriSatPairWithinDaysFromEvents = (events, days = 30) => {
  const today = new Date(); today.setHours(0,0,0,0);
  const end   = new Date(today); end.setDate(end.getDate() + days);

  // apu: YYYY-MM-DD
  const toYmd = (dt) => {
    const y = dt.getFullYear();
    const m = String(dt.getMonth()+1).padStart(2,"0");
    const d = String(dt.getDate()).padStart(2,"0");
    return `${y}-${m}-${d}`;
  };

  for (let dt = new Date(today); dt <= end; dt.setDate(dt.getDate()+1)) {
    // 5=Perjantai, 6=Lauantai (getDay: 0=Su)
    if (dt.getDay() !== 5) continue;

    const fri = toYmd(dt);
    const satDt = new Date(dt); satDt.setDate(satDt.getDate()+1);
    if (satDt > end) break; // la menee ikkunan yli
    const sat = toYmd(satDt);

    const friSt = getStatusOnDate(events, fri);
    const satSt = getStatusOnDate(events, sat);

    if (STATUS_OK.has(friSt) && STATUS_OK.has(satSt)) {
      return true;
    }
  }
  return false;
};


 // --- YMD min/max & viikonpäivä ---------------------------------
const ymdMin = useCallback((a, b) => (!a ? b : !b ? a : (a <= b ? a : b)), []);
const ymdMax = useCallback((a, b) => (!a ? b : !b ? a : (a >= b ? a : b)), []);

const isFriday = useCallback((ymd) => {
  const [y,m,d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay() === 5; // 5 = pe
}, []);

  // // --- apurit (UTC) ---
const ymdPlusDays = useCallback((ymd, days = 1) => {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}, []);

// Päivän "paras" status jo on olemassa (getStatusOnDate)
// Lisää tämän perään:
const hasStatusWithinDaysFromEvents = React.useCallback(
  (events, days = 30, targets = new Set(["pending","booked"])) => {
    const today = new Date(); today.setHours(0,0,0,0);
    const end   = new Date(today); end.setDate(end.getDate() + days);

    const toYmd = (dt) => {
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, "0");
      const d = String(dt.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    };

    for (let dt = new Date(today); dt <= end; dt.setDate(dt.getDate() + 1)) {
      const ymd = toYmd(dt);
      const st = getStatusOnDate(events, ymd); // "booked" | "pending" | "open" | "blocked" | null
      if (targets.has(st)) return true;
    }
    return false;
  },
  [getStatusOnDate]
);

// Normalisoi arvo (YYYY-MM-DD | ISO | Date) -> "YYYY-MM-DD"
const toYmd = useCallback((val) => {
  if (!val) return "";
  if (typeof val === "string") return val.slice(0, 10);
  if (val instanceof Date) {
    const yy = val.getUTCFullYear();
    const mm = String(val.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(val.getUTCDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  }
  return "";
}, []);

// Yksi tapa rajata 45 päivään
const isWithinDays = useCallback((val, days = WINDOW_DAYS) => {
  const ymd = toYmd(val);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return false;
  const upper = ymdPlusDays(todayStr, days);
  return ymd >= todayStr && ymd <= upper;
}, [todayStr, ymdPlusDays, toYmd]);

// Pe tai La
const isFriOrSat = useCallback((val) => {
  const ymd = toYmd(val);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return false;
  const [y, m, d] = ymd.split("-").map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0=Su … 5=Pe, 6=La
  return dow === 5 || dow === 6;
}, [toYmd]);

// ===== Haku + visualisointi =====
useEffect(() => {
  if (!bandId && !companyId) return;
  if (!viewRange.start || !viewRange.end) return;

  const ac = new AbortController();

  (async () => {
    try {
      const common = { from: viewRange.start, to: viewRange.end, signal: ac.signal };

      // 1) Kalenteri (näytettävät taustavärit)
      const calPromise = companyId
        ? api.companyCalendar(companyId, common)
        : api.calendar(bandId, common);

      // 2) Saatavuus: varmista että meillä on AINAKIN [today .. today+30] verhoa varten
      //    + lisäksi näkyvän näkymän loppuun asti + WINDOW_DAYS maalausta varten
      const availFrom = ymdMin(viewRange.start, todayStr);

      // HUOM: availability endpoint käyttää day < to (ekskl.). Pyydä +31, jotta "30. päivä" sisältyy.
      const mustCoverTo = ymdPlusDays(todayStr, 31);
      const availTo = ymdMax( ymdPlusDays(viewRange.end, WINDOW_DAYS), mustCoverTo );

      const availPromise = companyId
        ? Promise.resolve({ availability: {}, meta: {} })
        : api.availability.list(bandId, {
            from: availFrom,
            to:   availTo,
            signal: ac.signal,
          });


      const [calData, availDataRaw] = await Promise.all([calPromise, availPromise]);

      // Saatavuuskartan talteen (maalataan vain ruudukkaan osuvat päivät)
      const fullAvailMap = (availDataRaw && availDataRaw.availability) || {};
      const paintAvail = {};
      for (const d of Object.keys(fullAvailMap)) {
        if (d >= viewRange.start && d < viewRange.end) paintAvail[d] = true;
      }
      availRef.current = paintAvail;

      // Earliest meta talteen tarvittaessa
      const earliestFromAvail =
        availDataRaw?.meta?.earliest_open_ymd || earliestOpenRef.current || null;
      earliestOpenRef.current = earliestFromAvail;

      const src = Array.isArray(calData?.events) ? calData.events : [];
      const nowTs = Date.now();

      // normalisoi end seuraavan päivän alkuun (UTC)
      const normalized = src.map((ev) => {
        const status = ev.status || ev?.extendedProps?.status || "open";
        const start = toYmd(ev.start);
        let end = toYmd(ev.end);
        if (!end || end === start) end = ymdPlusDays(start, 1);
        return { ...ev, start, end, extendedProps: { ...(ev.extendedProps || {}), status } };
      });

      // siivoa vanhentuneet pendingit
      const filtered = normalized.filter((ev) => {
        const st = ev?.extendedProps?.status;
        if (st !== "pending") return true;
        const exp = ev?.extendedProps?.expires_at;
        if (exp) return new Date(exp).getTime() > nowTs;
        const sent = ev?.extendedProps?.sent_at;
        if (sent) return new Date(sent).getTime() + 24 * 60 * 60 * 1000 > nowTs;
        return true;
      });

      // status-taustat
      const busyBg = filtered.map((ev) => {
        const st = ev?.extendedProps?.status;
        if (st === "booked")
          return { ...ev, display: "background", allDay: true, color: "#ef4444", classNames: ["st-booked"] };
        if (st === "pending")
          return { ...ev, display: "background", allDay: true, color: "#fde047", classNames: ["st-pending"] };
        if (st === "blackout")
          return { ...ev, display: "background", allDay: true, color: "#9ca3af", classNames: ["st-blackout"] };
        if (st === "open")
          return { ...ev, display: "background", allDay: true, color: "#3CB371", classNames: ["st-open"] };
        return ev;
      });

      // availability-openit, jos ei busyä
      const busySet = new Set(
        busyBg
          .filter((ev) => ["booked", "pending", "blackout"].includes(ev?.extendedProps?.status))
          .map((ev) => ev.start)
      );

      const openFromAvail = [];
      for (const d of Object.keys(availRef.current)) {
        if (!availRef.current[d]) continue;
        if (busySet.has(d)) continue;
        openFromAvail.push({
          id: `open:${d}`,
          start: d,
          end: ymdPlusDays(d, 1),
          display: "background",
          allDay: true,
          color: "#86efac",
          classNames: ["st-open"],
          extendedProps: { status: "open" },
        });
      }

      const next = [...busyBg, ...openFromAvail];

  // --- DEBUG (vain dev): kevyt selite & numerot
  if (process.env.NODE_ENV !== 'production') {
    const explain = (d) => {
      const e = next.filter(x => x.start <= d && d < (x.end || x.start));
      const avail = !!availRef.current[d];
      const statuses = e.map(x => x?.extendedProps?.status || x.status);
      const blocking = e.find(x => ['booked','pending','blackout'].includes(x?.extendedProps?.status));
      return {
        date: d,
        inWindow: isWithinDays(d, WINDOW_DAYS),
        isFriOrSat: isFriOrSat(d),
        availabilityFlag: avail,
        eventCount: e.length,
        statuses,
        blockedBy: blocking?.extendedProps?.status || null,
      };
    };

    console.table([explain('2025-11-21'), explain('2025-11-22')]);
    window.__gigcal = {
      viewRange,
      openFromAvailCount: openFromAvail.length,
      busyBgCount: busyBg.length,
      availabilityCount: Object.keys(availRef.current).length,
    };
  }


      // vältetään turha render
      const sig = JSON.stringify(
        next.map((e) => ({ s: e.start, e: e.end, st: e?.extendedProps?.status, c: e.color }))
      );

      if (sig !== prevSigRef.current) {
        prevSigRef.current = sig;
        setEvents(next);

        // Verho auki jos: (a) yksikin pending/varattu TAI (b) löytyy pe–la -pari 30 pv sisällä
        const hasPair = hasFriSatPairWithinDaysFromEvents(next, 30);
        const hasOnePendingOrBooked = hasStatusWithinDaysFromEvents(
          next,
          30,
          new Set(["pending", "booked"])
        );
        onHasMarkedChangeRef.current?.(hasOnePendingOrBooked || hasPair);
      }
    } catch (err) {
      if (err.name !== "AbortError") console.error("Virhe kalenteridatan haussa:", err);
    }
  })();

  return () => ac.abort();
}, [
  bandId, companyId, refreshKey, viewRange,
  toYmd, ymdPlusDays,
  ymdMin, ymdMax, todayStr,
  hasFriSatPairWithinDaysFromEvents, // verhoehto (pe–la -pari)
  hasStatusWithinDaysFromEvents,     // verhoehto (yksikin pending/booked)
  isWithinDays, isFriOrSat // vain debugiin / luettavuuteen
]);

console.log(`${WINDOW_DAYS} pv yläraja:`, ymdPlusDays(todayStr, WINDOW_DAYS));

  // ===== selection highlight =====
  const applySelectionHighlight = () => {
    cellsRef.current.forEach((el) => el.classList.remove("fc-selected-day"));
    if (!selectedDate) return;
    const el = cellsRef.current.get(selectedDate);
    if (el) el.classList.add("fc-selected-day");
  };
    useEffect(applySelectionHighlight, [selectedDate, viewRange]);
  // Re-applaa valinta myös tausta-eventtien (events) päivittymisen jälkeen
  useEffect(() => {
    const t = setTimeout(applySelectionHighlight, 0);
    return () => clearTimeout(t);
  }, [events]);

  // ===== Klikki =====
  const handleDateClick = (arg) => {
    const clickedDate = new Date(arg.dateStr);
    const today = new Date(new Date().toDateString());
    if (clickedDate < today) return;

    const d = arg.dateStr;
    const ev = events.find((e) => e.start <= d && d < (e.end || e.start));

    let st = ev?.extendedProps?.status || null;
    const sentAt  = ev?.extendedProps?.sent_at    || null;
    const expires = ev?.extendedProps?.expires_at || null;

    // jos eventtiä ei ole → päättele availabilitysta
    if (!st) st = availRef.current[d] ? "open" : "closed";

    // tee päivästä “aktiivinen” vain jos se on vapaana
    if (st === "open") {
 // 1) tila talteen (säilyy myös re-renderissä)
      setSelectedDate(d);
      // 2) pysyvä valinta heti DOM:iin + kevyt ping
      const cell = cellsRef.current.get(d);
      if (cell) {
        // poista aiempi valinta muista soluista
        cellsRef.current.forEach((el) => el.classList.remove("fc-selected-day"));
        // pysyvä korostus + hetkellinen ping
        cell.classList.add("fc-selected-day");
        cell.classList.add("fc-selected-ping");
        setTimeout(() => cell.classList.remove("fc-selected-ping"), 350);
      }
      // mobiilissa rullaa lomakkeeseen
      if (enableMobileScroll && typeof window !== "undefined" && window.matchMedia("(max-width: 767.98px)").matches) {
        requestAnimationFrame(() => {
          const sel = scrollTargetSelector || '#contact-form, [data-contact-form]';
          const el = document.querySelector(sel);
          if (!el) return;
          const header =
            document.querySelector('[data-sticky-header]') ||
            document.querySelector('header.sticky') ||
            document.querySelector('header.fixed') ||
            document.querySelector('.site-header.sticky');
          const headerH = header ? header.getBoundingClientRect().height : 0;
          const OFFSET_BELOW = -48;
          const y = window.scrollY + el.getBoundingClientRect().top - headerH + OFFSET_BELOW;
          window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
          const firstField = el.querySelector('[data-scroll-anchor], input, select, textarea, button');
          firstField?.focus({ preventScroll: true });
        });
      }
    }

    if (st === "blackout") return onDateSelect?.(d, "blackout", sentAt, expires);
    if (st === "booked")   return onDateSelect?.(d, "booked",   sentAt, expires);
    if (st === "pending")  return onDateSelect?.(d, "pending",  sentAt, expires);
    return onDateSelect?.(d, st, sentAt, expires); // open / closed
  };

  // varaklikki jos jokin peittää solun
  const dayCellDidMount = (info) => {
    const todayMidnight = new Date(new Date().toDateString());
    if (info.date < todayMidnight) {
      info.el.style.pointerEvents = "none";
      info.el.style.opacity = "0.4";
      return;
    }
    const y = info.date.getFullYear();
    const m = String(info.date.getMonth() + 1).padStart(2, "0");
    const d = String(info.date.getDate()).padStart(2, "0");
    const iso = `${y}-${m}-${d}`;

    info.el.dataset.iso = iso;
    cellsRef.current.set(iso, info.el);
    if (selectedDate === iso) info.el.classList.add("fc-selected-day");

    info.el.addEventListener("click", (e) => {
      if (e.target.closest(".fc-daygrid-event")) return;
      handleDateClick({ dateStr: iso });
    });
  };

  const dayCellClassNames = (arg) => {
    const todayMidnight = new Date(new Date().toDateString());
    if (arg.date < todayMidnight) return ["fc-disabled-day"];
    return ["cursor-pointer", "hover:bg-pink-300", "transition-colors"];
  };

  return (
    <div ref={containerRef} className="flex flex-col rounded-xl p-4 bg-electric/50 dark:bg-night-700/50 gigcal">
      {/* ---- Ulkoasu & ohjetekstit ---- */}
      <style>{`
        .gigcal .fc .fc-bg-event { opacity: 1 !important; pointer-events: none !important; z-index: 0; }
         /* sallitaan pseudo-elementtien näkyä solun yli */
        .gigcal .fc .fc-daygrid-day-frame { position: relative; z-index: 1; overflow: visible; }
        .gigcal .fc .fc-daygrid-day-number { pointer-events: none; }

        .gigcal .fc .fc-toolbar-title { color: #1f2937 !important; font-weight: 700; text-shadow: none; }
        .dark .gigcal .fc .fc-toolbar-title { color: #ffffff !important; text-shadow: 1px 1px 2px rgba(0,0,0,.4); }

        .gigcal .fc .fc-daygrid-event .fc-event-main,
        .gigcal .fc .fc-daygrid-event .fc-event-title,
        .gigcal .fc .fc-daygrid-event .fc-event-time { display: none !important; }
        .gigcal .fc .fc-daygrid-event { background: transparent !important; border: 0 !important; box-shadow: none !important; }

        .gigcal .fc .fc-col-header-cell-cushion { color: #1f2937 !important; font-weight: 600; }
        .dark .gigcal .fc .fc-col-header-cell-cushion { color: #000000 !important; }

        .gigcal .fc .fc-daygrid-day-number { color: #1f2937 !important; }
        .dark .gigcal .fc .fc-daygrid-day-number { color: #ffffff !important; font-weight: 600; text-shadow: 0 0 4px rgba(0,0,0,0.6); }

        .gigcal .fc .fc-daygrid-day.fc-day-today {
          position: relative;
          background: rgba(59,130,246,0.10) !important;
          outline: 2px solid rgba(59,130,246,0.55);
          outline-offset: -2px;
        }
        .dark .gigcal .fc .fc-daygrid-day.fc-day-today {
          background: rgba(59,130,246,0.18) !important;
          outline-color: rgba(96,165,250,0.9);
        }
        .gigcal .fc .fc-daygrid-day.fc-day-today .fc-daygrid-day-number { color: #0b1020 !important; font-weight: 700; }
        .dark .gigcal .fc .fc-daygrid-day.fc-day-today .fc-daygrid-day-number { color: #ffffff !important; text-shadow: 0 0 6px rgba(0,0,0,0.7); }
        .gigcal .fc .fc-daygrid-day.fc-day-today::after {
          content: "Tänään";
          position: absolute; bottom: 30px; right: 4px;
          font-size: 10px; line-height: 1; padding: 2px 6px; border-radius: 999px;
          background: #60a5fa; color: #0b1020; pointer-events: none;
        }
        .dark .gigcal .fc .fc-daygrid-day.fc-day-today::after { background: #93c5fd; color: #0b1020; }

        .gigcal .fc-event { padding: 2px 4px; font-size: 11px; font-weight: 600; text-align: center; }

        .gigcal .fc .st-booked   { background-color:#ef4444 !important; }
        .gigcal .fc .st-pending  { background-color:#f59e0b !important; }
        .gigcal .fc .st-blackout { background-color:#9ca3af !important; }
        .gigcal .fc .st-open     { background-color:#3CB371 !important; }

        .gigcal .fc .fc-button.fc-prev-button,
        .gigcal .fc .fc-button.fc-next-button {
          width: 28px; height: 28px; border-radius: 50%; padding: 0; gap: 5px;
          background: #374151; border: none; display: flex; align-items: center; justify-content: center;
        }
        .gigcal .fc .fc-button.fc-prev-button:hover,
        .gigcal .fc .fc-button.fc-next-button:hover { background: #4b5563; }
        .gigcal .fc .fc-button.fc-prev-button .fc-icon,
        .gigcal .fc .fc-button.fc-next-button .fc-icon { font-size: 12px; color: #fff; }

        .gigcal .fc-selected-day { position: relative; }
        /* 🐜 “muurahaiset” – marssivat reunaviivat */
        .gigcal .fc-selected-day .fc-daygrid-day-frame::after {
          content: "";
          position: absolute; inset: 2px;
          border-radius: 10px;
          background:
            linear-gradient(90deg, #22d3ee 50%, transparent 0) repeat-x,
            linear-gradient(90deg, #22d3ee 50%, transparent 0) repeat-x,
            linear-gradient(0deg,  #22d3ee 50%, transparent 0) repeat-y,
            linear-gradient(0deg,  #22d3ee 50%, transparent 0) repeat-y;
          background-size: 8px 2px, 8px 2px, 2px 8px, 2px 8px;
          background-position: left top, left bottom, left top, right top;
          animation: k-ants .75s linear infinite;
          box-shadow: 0 0 10px rgba(34,211,238,.60), 0 0 0 3px rgba(34,211,238,.25);
          pointer-events: none; z-index: 3;
        }
        .dark .gigcal .fc-selected-day .fc-daygrid-day-frame::after {
          background:
            linear-gradient(90deg, #7dd3fc 50%, transparent 0) repeat-x,
            linear-gradient(90deg, #7dd3fc 50%, transparent 0) repeat-x,
            linear-gradient(0deg,  #7dd3fc 50%, transparent 0) repeat-y,
            linear-gradient(0deg,  #7dd3fc 50%, transparent 0) repeat-y;
        }
        .gigcal .fc-selected-day .fc-daygrid-day-frame::before {
          content: "";
          position: absolute; inset: 6px;
          background: rgba(34,211,238,.18); border-radius: 8px;
          pointer-events: none; z-index: 2;
        }
        .dark .gigcal .fc-selected-day .fc-daygrid-day-frame::after {
          background:
            linear-gradient(90deg, #7dd3fc 50%, transparent 0) repeat-x,
            linear-gradient(90deg, #7dd3fc 50%, transparent 0) repeat-x,
            linear-gradient(0deg,  #7dd3fc 50%, transparent 0) repeat-y,
            linear-gradient(0deg,  #7dd3fc 50%, transparent 0) repeat-y;
        }
        /* Kevyt täyttö korostamaan valintaa */
        .gigcal .fc-selected-day .fc-daygrid-day-frame::before {
          content: "";
          position: absolute; inset: 6px;
          background: rgba(34,211,238,.18); border-radius: 8px;
          pointer-events: none; z-index: 2;
        }
        .gigcal .fc-selected-day .fc-daygrid-day-number {
          color: #0b1020 !important; font-weight: 800; text-shadow: none;
        }

        @keyframes k-ping { 0% { transform: scale(.96); opacity: .9; } 100% { transform: scale(1.06); opacity: 0; } }
        .gigcal .fc-selected-ping::after {
          content: "";
          position: absolute; inset: 4px; border-radius: 8px;
          border: 2px solid rgba(34,211,238,.75);
          animation: k-ping .35s ease-out 1; pointer-events: none; z-index: 4;
        }
          @keyframes k-ants {
          100% {
            background-position:
              8px 0,
              -8px 100%,
              0 8px,
              100% -8px;
          }
        }
        .gigcal .fc-selected-day:hover { background: transparent !important; }
      `}</style>

      <p className="dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm pb-4">
        Valitse kalenterista <strong>vapaa päivä</strong> ja täytä viereinen yhteydenottolomake.{" "}
        Värikoodit: <span className="text-green-500 font-bold">vihreä</span> = buukattavissa,{" "}
        <span className="text-yellow-400 font-bold">keltainen</span> = odottaa tarjouksen hyväksyntää,{" "}
        <span className="text-red-500 font-bold">punainen</span> = varattu.
      </p>

      <FullCalendar
        plugins={[dayGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        events={events}
        eventContent={() => null}
        eventDidMount={(info) => {
          if (info?.el?.removeAttribute) info.el.removeAttribute("title");
        }}
        locale={fiLocale}
        datesSet={(arg) => {
          const start = arg.startStr.slice(0, 10);
          const end   = arg.endStr.slice(0, 10);
          const last = lastRangeRef.current;
          if (last.start === start && last.end === end) return;

          lastRangeRef.current = { start, end };
          cellsRef.current = new Map();
          setViewRange({ start, end });
        }}

        dateClick={handleDateClick}
        dayCellClassNames={dayCellClassNames}
        dayCellDidMount={dayCellDidMount}
        initialDate={calendarInitialDate}
        validRange={{ start: monthStartStr }}
        height="auto"
        contentHeight="auto"
        expandRows
        handleWindowResize
      />
    </div>
  );
};

Keikkakalenteri.propTypes = {
  bandId:    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onDateSelect: PropTypes.func,
  refreshKey:   PropTypes.number,
  initialDate:  PropTypes.string,
  enableMobileScroll: PropTypes.bool,
  scrollTargetSelector: PropTypes.string,
  onHasMarkedChange: PropTypes.func,
};

export default Keikkakalenteri;
